import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, FileText } from 'lucide-react';
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-737">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-738">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-739">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-740">
            <span className="w-8 h-px bg-teal" data-id="element-741"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-742">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-743"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-744">
            Privacy Policy
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-745">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-746">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-747">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-748">
            At SyNeural, we are committed to protecting your privacy and
            handling your personal information with care and transparency. This
            Privacy Policy explains how we collect, use, store, and protect your
            information when you use our platform, website, and services.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-749">
            By using SyNeural's services, you agree to the practices described
            in this Privacy Policy. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-750">
        {/* Information We Collect */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-751">
          <div className="flex items-center gap-4 mb-6" data-id="element-752">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-753">
              <Database size={24} data-id="element-754" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-755">
              Information We Collect
            </h2>
          </div>

          <div className="space-y-6" data-id="element-756">
            <div data-id="element-757">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-758">
                Personal Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-759">
                We may collect personal information that you provide directly to
                us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-760">
                <li data-id="element-761">Name, email address, and contact information</li>
                <li data-id="element-762">Organization or institutional affiliation</li>
                <li data-id="element-763">Account credentials and authentication information</li>
                <li data-id="element-764">Profile information and preferences</li>
                <li data-id="element-765">Communication history and support inquiries</li>
              </ul>
            </div>

            <div data-id="element-766">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-767">
                Assessment and Platform Data
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-768">
                When you use our CJI and NCJI assessment tools, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-769">
                <li data-id="element-770">Assessment responses and performance data</li>
                <li data-id="element-771">Reasoning patterns and decision-making metrics</li>
                <li data-id="element-772">Development pathway selections and progress tracking</li>
                <li data-id="element-773">
                  Aggregated and anonymized capability data for research
                  purposes
                </li>
              </ul>
            </div>

            <div data-id="element-774">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-775">
                Technical Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-776">
                We automatically collect certain technical information,
                including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-777">
                <li data-id="element-778">Device information, browser type, and operating system</li>
                <li data-id="element-779">IP address and general location data</li>
                <li data-id="element-780">Usage data, page views, and interaction patterns</li>
                <li data-id="element-781">Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* How We Use Your Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-782">
          <div className="flex items-center gap-4 mb-6" data-id="element-783">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-784">
              <Eye size={24} data-id="element-785" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-786">
              How We Use Your Information
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-787">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-788">
            <li data-id="element-789">To provide, maintain, and improve our platform and services</li>
            <li data-id="element-790">
              To deliver personalized assessment results, insights, and
              development recommendations
            </li>
            <li data-id="element-791">
              To conduct research and analysis to advance our understanding of
              judgment, decision-quality, and human capability
            </li>
            <li data-id="element-792">
              To communicate with you about your account, updates, and support
            </li>
            <li data-id="element-793">To ensure platform security and prevent fraud or misuse</li>
            <li data-id="element-794">
              To comply with legal obligations and enforce our terms of service
            </li>
            <li data-id="element-795">
              To develop new features, tools, and institutional applications
            </li>
          </ul>
        </motion.div>

        {/* Data Sharing and Disclosure */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-796">
          <div className="flex items-center gap-4 mb-6" data-id="element-797">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-798">
              <UserCheck size={24} data-id="element-799" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-800">
              Data Sharing and Disclosure
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-801">
            We do not sell your personal information. We may share your
            information in the following circumstances:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-802">
            <li data-id="element-803">
              <strong data-id="element-804">With Your Consent:</strong> When you explicitly authorize
              us to share your information
            </li>
            <li data-id="element-805">
              <strong data-id="element-806">Institutional Partners:</strong> If you access SyNeural
              through an educational institution, employer, or government
              program, we may share relevant data with that organization as
              agreed upon
            </li>
            <li data-id="element-807">
              <strong data-id="element-808">Service Providers:</strong> With trusted third-party
              vendors who help us operate our platform (e.g., hosting,
              analytics, customer support)
            </li>
            <li data-id="element-809">
              <strong data-id="element-810">Research Partners:</strong> Aggregated, anonymized data
              may be shared with research institutions for academic and
              scientific purposes
            </li>
            <li data-id="element-811">
              <strong data-id="element-812">Legal Requirements:</strong> When required by law, court
              order, or to protect our rights and safety
            </li>
            <li data-id="element-813">
              <strong data-id="element-814">Business Transfers:</strong> In connection with a merger,
              acquisition, or sale of assets
            </li>
          </ul>
        </motion.div>

        {/* Data Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-815">
          <div className="flex items-center gap-4 mb-6" data-id="element-816">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-817">
              <Lock size={24} data-id="element-818" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-819">
              Data Security
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-820">
            We implement industry-standard security measures to protect your
            information, including:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-821">
            <li data-id="element-822">Encryption of data in transit and at rest</li>
            <li data-id="element-823">Secure authentication and access controls</li>
            <li data-id="element-824">Regular security audits and vulnerability assessments</li>
            <li data-id="element-825">Employee training on data protection and privacy practices</li>
            <li data-id="element-826">
              Incident response procedures and breach notification protocols
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-827">
            While we take reasonable precautions, no method of transmission over
            the internet or electronic storage is 100% secure. We cannot
            guarantee absolute security but are committed to protecting your
            information to the best of our ability.
          </p>
        </motion.div>

        {/* Your Rights and Choices */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-828">
          <div className="flex items-center gap-4 mb-6" data-id="element-829">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-830">
              <Shield size={24} data-id="element-831" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-832">
              Your Rights and Choices
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-833">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-834">
            <li data-id="element-835">
              <strong data-id="element-836">Access:</strong> Request a copy of the personal
              information we hold about you
            </li>
            <li data-id="element-837">
              <strong data-id="element-838">Correction:</strong> Request correction of inaccurate or
              incomplete information
            </li>
            <li data-id="element-839">
              <strong data-id="element-840">Deletion:</strong> Request deletion of your personal
              information (subject to legal obligations)
            </li>
            <li data-id="element-841">
              <strong data-id="element-842">Portability:</strong> Request a copy of your data in a
              structured, machine-readable format
            </li>
            <li data-id="element-843">
              <strong data-id="element-844">Opt-Out:</strong> Opt out of certain data processing
              activities, such as marketing communications
            </li>
            <li data-id="element-845">
              <strong data-id="element-846">Withdraw Consent:</strong> Withdraw consent for data
              processing where consent is the legal basis
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-847">
            To exercise these rights, please contact us at{' '}
            <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-848">
              privacy@syneural.com
            </a>
            .
          </p>
        </motion.div>

        {/* Data Retention */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-849">
          <div className="flex items-center gap-4 mb-6" data-id="element-850">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-851">
              <FileText size={24} data-id="element-852" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-853">
              Data Retention
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-854">
            We retain your personal information for as long as necessary to
            provide our services, comply with legal obligations, resolve
            disputes, and enforce our agreements. Assessment data may be
            retained longer for research and platform improvement purposes, but
            will be anonymized where possible. You may request deletion of your
            account and associated data at any time, subject to legal retention
            requirements.
          </p>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-855">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-856">
            Children's Privacy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-857">
            SyNeural's services are not directed to children under 13 years of
            age. We do not knowingly collect personal information from children
            under 13. If we become aware that we have collected information from
            a child under 13, we will take steps to delete such information. For
            educational use cases involving minors, we work with schools and
            institutions to ensure appropriate parental consent and data
            protection measures are in place.
          </p>
        </motion.div>

        {/* International Data Transfers */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-858">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-859">
            International Data Transfers
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-860">
            Your information may be transferred to and processed in countries
            other than your country of residence. We ensure that such transfers
            comply with applicable data protection laws and implement
            appropriate safeguards, such as standard contractual clauses, to
            protect your information.
          </p>
        </motion.div>

        {/* Changes to This Policy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-861">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-862">
            Changes to This Privacy Policy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-863">
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technology, legal requirements, or other
            factors. We will notify you of material changes by posting the
            updated policy on our website and updating the "Last Updated" date.
            Your continued use of our services after such changes constitutes
            acceptance of the updated policy.
          </p>
        </motion.div>

        {/* Contact Us */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-864">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-865">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-866">
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-867">
            <p data-id="element-868">
              <strong data-id="element-869">Email:</strong>{' '}
              <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-870">
                privacy@syneural.com
              </a>
            </p>
            <p data-id="element-871">
              <strong data-id="element-872">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-873">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}